
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, RefreshCw } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
    DndContext,
    pointerWithin,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from '@dnd-kit/core';
import {
    sortableKeyboardCoordinates,
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { LineItemMatchGroupCard } from './LineItemMatchGroupCard';
import { UnmatchedLineItemsSection } from './UnmatchedLineItemsSection';
import { HydratedMatchGroup } from '../types';
import { LineItemWithMatchInfo } from '@/api/generated/client/models/LineItemWithMatchInfo';
import { EnhancedRoomMatch } from '../types';
import { DraggableLineItem } from './DraggableLineItem';
import { ComparisonFilters } from "@/hooks/useComparisonState";
import { parseLineItemNum, compareLineItems } from "../utils";

interface CollapsibleLineItemRoomCardProps {
    match: EnhancedRoomMatch;
    allGroups: HydratedMatchGroup[]; // All groups for this room
    onApproveGroup: (groupId: number) => void;
    onRejectGroup: (groupId: number) => void;
    onRematchRoom: (roomId: number) => void;
    onDragEnd: (event: DragEndEvent, roomId: number) => void;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    filters: ComparisonFilters;
    isRoomMatch: boolean;
    selectedItemIds?: Set<number>;
    onToggleItemSelection?: (itemId: number) => void;
    onAddSelectedToGroup?: (groupId: number, selectedIds: number[]) => void;
    selectedItemsRoomId?: number | null; // The room ID that contains the currently selected items
}

export function CollapsibleLineItemRoomCard({
    match,
    allGroups,
    onApproveGroup,
    onRejectGroup,
    onRematchRoom,
    onDragEnd,
    isOpen,
    onOpenChange,
    filters,
    isRoomMatch,
    selectedItemIds,
    onToggleItemSelection,
    onAddSelectedToGroup,
    selectedItemsRoomId
}: CollapsibleLineItemRoomCardProps) {
    const [activeItem, setActiveItem] = useState<LineItemWithMatchInfo | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const roomIds = new Set([
        ...(match.insurerDetails?.map(r => r.room.id) || []),
        ...(match.contractorDetails?.map(r => r.room.id) || [])
    ]);

    const allInsurerItems = match.insurerDetails?.flatMap(d => d.items) || [];
    const allContractorItems = match.contractorDetails?.flatMap(d => d.items) || [];

    const relevantGroups = allGroups.filter(g =>
        g.insurerItems.some(i => i.roomId && roomIds.has(i.roomId)) ||
        g.contractorItems.some(i => i.roomId && roomIds.has(i.roomId))
    );

    const approvedCount = relevantGroups.filter(g => g.status === 'approved' || g.status === 'user_confirmed').length;

    const unmatchedInsurer = allInsurerItems.filter(i => !i.lineItemMatchGroupId) as LineItemWithMatchInfo[];
    const unmatchedContractor = allContractorItems.filter(i => !i.lineItemMatchGroupId) as LineItemWithMatchInfo[];

    const activeTerms = [filters.searchQuery, ...filters.searchTerms].filter(t => t.trim() !== '').map(t => t.toLowerCase());
    const filterFn = (text: string) => {
        if (activeTerms.length === 0) return true;
        return activeTerms.some(term => text.toLowerCase().includes(term));
    };

    const isItemVisible = (item: LineItemWithMatchInfo) => {
        return filterFn(item.description || '');
    };

    // Filter Groups
    const filteredGroups = relevantGroups.filter(g => {
        // 1. Hide Matched Filter
        if (!filters.showMatched && g.status === 'approved') return false;

        // 2. Search Filter (if any item in group matches OR if room matches)
        const hasMatch = isRoomMatch ||
            g.insurerItems.some(i => isItemVisible(i)) ||
            g.contractorItems.some(i => isItemVisible(i));

        return hasMatch;
    });

    // Sort match groups by the minimum insurer lineItemNumber in each group
    const sortedFilteredGroups = [...filteredGroups].sort((a, b) => {
        const minA = a.insurerItems.length > 0
            ? Math.min(...a.insurerItems.map(i => parseLineItemNum(i.lineItemNumber)))
            : Infinity;
        const minB = b.insurerItems.length > 0
            ? Math.min(...b.insurerItems.map(i => parseLineItemNum(i.lineItemNumber)))
            : Infinity;
        if (minA !== minB) return minA - minB;
        return a.id - b.id; // Fallback to id if same lineItemNumber
    });

    // Filter Unmatched
    const filteredUnmatchedInsurer = (isRoomMatch ? unmatchedInsurer : unmatchedInsurer.filter(i => isItemVisible(i)))
        .sort(compareLineItems);
    const filteredUnmatchedContractor = (isRoomMatch ? unmatchedContractor : unmatchedContractor.filter(i => isItemVisible(i)))
        .sort(compareLineItems);

    const totalInsurer = allInsurerItems.reduce((sum, i) => sum + (i.rcvCents || 0), 0);
    const totalContractor = allContractorItems.reduce((sum, i) => sum + (i.rcvCents || 0), 0);

    const insurerNames = match.roomA?.join(', ') || 'Unknown';
    const contractorNames = match.roomB?.join(', ') || 'Unknown';

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const itemId = Number(active.id);
        const item =
            allInsurerItems.find(i => i.lineItemId === itemId) ||
            allContractorItems.find(i => i.lineItemId === itemId);
        if (item) setActiveItem(item as LineItemWithMatchInfo);
    };

    const handleDragEndInternal = (event: DragEndEvent) => {
        setActiveItem(null);
        onDragEnd(event, match.id);
    };

    const handleAddSelectedToGroup = (groupId: number, selectedIds: number[]) => {
        if (onAddSelectedToGroup) {
            onAddSelectedToGroup(groupId, selectedIds);
        }
    };

    // Simple check: if items are selected from another room, disable interaction
    const hasSelectedItemsInThisRoom = selectedItemsRoomId === null || selectedItemsRoomId === match.id;

    return (
        <Card className="border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden mb-4">
            <Collapsible open={isOpen} onOpenChange={onOpenChange}>
                {/* Room Header */}
                <div className="flex flex-col lg:flex-row items-center justify-between p-4 bg-slate-50/50 dark:bg-slate-900/50 border-b gap-4">
                    <div className="flex items-center gap-3 w-full lg:w-auto">
                        <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-primary shrink-0">
                                <ChevronRight className={cn("h-5 w-5 transition-transform duration-200", isOpen && "rotate-90")} />
                            </Button>
                        </CollapsibleTrigger>

                        <div className="flex flex-col">
                            <div className="flex items-center flex-wrap gap-2">
                                <span className="font-semibold text-sm">{insurerNames}</span>
                                <span className="text-muted-foreground px-1">↔</span>
                                <span className="font-semibold text-sm text-blue-600 dark:text-blue-400">{contractorNames}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="text-[10px] h-5 font-normal bg-background border-slate-200 dark:border-slate-800">
                                    {approvedCount} of {relevantGroups.length} approved
                                </Badge>
                                <span className="text-xs">
                                    Total: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalInsurer / 100)} / {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalContractor / 100)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button size="sm" className="h-8 gap-2" onClick={() => onRematchRoom(match.id)}>
                            <RefreshCw className="h-3.5 w-3.5" />
                            Rematch Items
                        </Button>
                    </div>
                </div>

                <CollapsibleContent>
                    <div className="p-4 bg-slate-100/30 dark:bg-slate-950/30 min-h-[100px]">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={pointerWithin}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEndInternal}
                            accessibility={{ restoreFocus: false }}
                        >
                            <div className="space-y-4">
                                {/* Match Groups */}
                                <SortableContext items={sortedFilteredGroups.map(g => g.id)} strategy={verticalListSortingStrategy}>
                                    {sortedFilteredGroups.map(group => (
                                        <LineItemMatchGroupCard
                                            key={group.id}
                                            group={group}
                                            onApprove={() => onApproveGroup(group.id)}
                                            onReject={() => onRejectGroup(group.id)}
                                            roomName={insurerNames}
                                            selectedItemIds={selectedItemIds}
                                            onAddSelectedItems={(groupId) => {
                                                const selectedArray = Array.from(selectedItemIds || []);
                                                handleAddSelectedToGroup(groupId, selectedArray);
                                            }}
                                            canAddSelected={hasSelectedItemsInThisRoom}
                                        />
                                    ))}
                                </SortableContext>

                                {/* Unmatched Section */}
                                <UnmatchedLineItemsSection
                                    insurerItems={filteredUnmatchedInsurer}
                                    contractorItems={filteredUnmatchedContractor}
                                    roomId={match.id}
                                    selectedItemIds={selectedItemIds}
                                    onToggleItemSelection={onToggleItemSelection}
                                    hasSelectedItemsInThisRoom={hasSelectedItemsInThisRoom}
                                />
                            </div>

                            <DragOverlay>
                                {activeItem ? (
                                    <DraggableLineItem
                                        item={activeItem}
                                        isSelected={false}
                                        onToggle={() => { }}
                                        isLocked={false}
                                        isOverlay
                                        showCheckbox={false}
                                    />
                                ) : null}
                            </DragOverlay>
                        </DndContext>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
}
